import { Injectable } from '@angular/core';
import { AppConfig } from '../app.config';
import { environment } from '../../environments/environment';
import { ReportSentDataRequest } from '../_models/reports/report-sent-data-request.model';
import { catchError, map, retry } from 'rxjs/operators';
import { defer, of, throwError } from 'rxjs';
import { BaseService } from './base/base.service';
import { SessionUserHashManagerService } from './session-user-hash-manager/session-user-hash-manager.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { quietErrorsContext } from '../_helpers/error-support.interceptor';

@Injectable()
export class ReportService extends BaseService {
  
  private apiUrl: string = environment.apiUrl;

  constructor(
    private http: HttpClient, 
    private config: AppConfig,
    protected hashManagerService: SessionUserHashManagerService
    ) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith("/") ? "" : "/";
  }

  // The Reports filter lookups. Each is retried once on a dropped or server-side failure, and its failure
  // raises no support modal: the page shows an inline "Couldn't load" note instead (40760). The
  // interceptor still logs every failure to Application Insights.
  GetCoordinatorsForReport(param) {
    return this.filterLookup('GetCoordinatorsForReport', param);
  }

  GetTutorsForReport(param) {
    return this.filterLookup('GetTutorsForReport', param);
  }

  GetStudentsForReport(param) {
    return this.filterLookup('GetStudentsForReport', param);
  }

  GetReferrersForReport(param) {
    return this.filterLookup('GetReferrersForReport', param);
  }

  private filterLookup(operation: string, param) {
    // defer: a retry builds the request again, so it carries a fresh RequestId and RequestMatch.
    return defer(() => this.http.post(this.apiUrl + 'api/reports/' + operation, param,
      { ...this.prepareOptions(param), context: quietErrorsContext() })).pipe(
      retry({ count: 1, delay: (error: HttpErrorResponse) => isRetryable(error) ? of(true) : throwError(() => error) }),
      map((data: any) => data.body),
      catchError(this.handleError(operation))
    );
  }
  GetReports() {
    return this.http.get(this.apiUrl + 'api/reports/GetReports', this.prepareOptions(null)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('GetReports'))
    );
  }

  generateReportListToSend(param) {
    return this.http.post(this.apiUrl + 'api/reports/GetReportingSetFromFilter', param, this.prepareOptions(param)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('generateReportListToSend'))
    );
  }

  getGeneratedReports(request: ReportSentDataRequest) {
    return this.http.post(this.apiUrl + 'api/reports/ViewGeneratedReports', request, this.prepareOptions(request)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('getGeneratedReports'))
    );
  }

}

/** A dropped request (status 0) or a server-side failure is worth one more try; a refusal is not. */
function isRetryable(error: HttpErrorResponse): boolean {
  return error?.status === 0 || error?.status >= 500;
}
